import { FC, ReactNode, SyntheticEvent, useCallback, useState } from 'react';
import { css, Theme, useTheme } from '@emotion/react';
import MuiAccordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { Question } from '@phosphor-icons/react';
import { CollapseIcon } from 'client/components/Other/CollapseIcon';
import { Typography } from 'client/components/Typography';
import { borderRadius, Easing, Font, gutterWidth, layoutStyles, mainWidth, mediaQuery, sideWidth } from 'client/Theme';

const heroStyle = (theme: Theme) => css`
  display: flex;
  position: relative;
  ${layoutStyles}
  padding: 4rem 0;

  &:after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    width: 100%;
    background-color: ${theme.color.grey[60]};
  }

  > aside {
    display: none;
    flex-shrink: 1;
    margin-right: ${gutterWidth}ch;
    width: ${sideWidth}ch;

    ${mediaQuery.large(css`
      display: initial;
    `)}

    svg {
      width: 100%;
      height: auto;
    }
  }

  > div {
    padding: 0 24px;
    align-self: center;
    width: ${mainWidth}ch;

    ${mediaQuery.large(css`
      padding: 0;
    `)}

    p {
      color: ${theme.color.foreground};
    }

    > header {
      margin-bottom: 1.5rem;

      > h4 {
        margin-bottom: 0.25rem;
        font-family: ${Font.header};
        font-weight: 500;
      }

      > p {
        color: ${theme.color.grey[100]};
      }
    }
  }
`;

const accordionStyle = (theme: Theme) => css`
  ${layoutStyles}
  margin-bottom: 1rem;
  padding: 0.6rem 0;
  background-color: ${theme.color.background};
  border-radius: ${borderRadius}px;

  box-shadow: none;

  &:before {
    content: none;
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    width: 100%;
    background-color: ${theme.color.grey[60]};
  }

  &:first-of-type .MuiAccordionSummary-content {
    margin-top: 0;
  }

  &.Mui-expanded {
    margin: 0 auto;
  }

  .MuiAccordion-root + & {
    margin-top: 0;

    &.Mui-expanded {
      margin-top: 0;
    }
  }

  .MuiAccordionDetails-root {
    padding: 0;

    > article {
      padding-top: 0;
      padding-bottom: 4.5rem;

      &:after {
        content: none;
      }
    }
  }

  .MuiCollapse-root {
    border-radius: 0 0 ${borderRadius}px ${borderRadius}px;
    overflow: hidden;
  }
`;

const accordionSummaryStyle = css`
  padding: 0;

  > .MuiAccordionSummary-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 1rem 0;

    transition: transform 300ms ${Easing.springy};

    &.Mui-expanded {
      margin: 1rem 0;

      > div,
      > svg:first-of-type {
        transform: translateY(-12px);
        opacity: 0;
      }
    }

    > span,
    > svg {
      margin: 0 18px;
    }

    > div,
    > svg:first-of-type {
      transition: opacity 400ms ${Easing.springy}, transform 400ms ${Easing.springy};
    }

    > div:first-of-type {
      flex: 1;
    }

    > div:last-of-type {
      justify-content: flex-end;
      width: 101px;
    }

    > svg:first-of-type {
      margin-left: 0;
      opacity: 0.6;
    }

    > .icon {
      margin-right: 0;
    }
  }
`;

export interface HeroProps {
  title: string | JSX.Element;
  subtitle?: string;
  illustration?: JSX.Element;
  children?: ReactNode;
}

export const Hero: FC<HeroProps> = ({ title, subtitle, illustration, children, ...other }) => (
  <article css={heroStyle} {...other}>
    <aside>{illustration}</aside>
    <div>
      <header>
        <Typography variant="h4">{title}</Typography>
        <Typography variant="body1" component="p">
          {subtitle}
        </Typography>
      </header>

      {children}
    </div>
  </article>
);

export interface HeroAccordionProps extends HeroProps {
  id: string;
  initialOpen?: boolean;
}

export const HeroAccordion: FC<HeroAccordionProps> = ({ id, initialOpen = false, title, subtitle, ...other }) => {
  const [openState, setOpenState] = useState(initialOpen);
  const theme = useTheme();

  const open = openState;

  const handleChange = useCallback((event: SyntheticEvent<Element, Event>, expanded: boolean) => {
    setOpenState(expanded);
  }, []);

  return (
    <MuiAccordion expanded={open} onChange={handleChange} css={accordionStyle}>
      <AccordionSummary id={`${id}-accordion-header`} aria-controls={`${id}-accordion-content`} css={accordionSummaryStyle}>
        <Question weight="duotone" size={24} color={theme.color.grey[100]} />
        <div>
          <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: '0rem' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" component="p">
              {subtitle}
            </Typography>
          )}
        </div>
        <CollapseIcon open={open} hardCloseIcon />
      </AccordionSummary>
      <AccordionDetails id={`${id}-accordion-content`}>
        <Hero title={title} subtitle={subtitle} {...other} />
      </AccordionDetails>
    </MuiAccordion>
  );
};
